import React, { useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import ErrorAlert from "../../../components/common/ErrorAlert";
import Spinner from "../../../components/common/Spinner";
import {
  fetchComplianceSummaryReport,
  fetchBuildingSalePack,
  fetchAnnualDirectorReport,
  ComplianceSummaryReport,
} from "../api";

interface SalePackData {
  entries: Array<{
    category: string;
    status: string;
    fileName?: string;
    downloadUrl?: string;
  }>;
}

interface AnnualDirectorReportData extends Record<string, unknown> {
  period: string;
  items: Array<{
    category: string;
    status: string;
  }>;
}

const ComplianceReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] =
    useState<ComplianceSummaryReport | null>(null);
  const [salePackData, setSalePackData] = useState<SalePackData | null>(null);
  const [annualData, setAnnualData] = useState<AnnualDirectorReportData | null>(
    null,
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadSummary = async (format: "json" | "pdf" = "json") => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchComplianceSummaryReport(format);
      if (format === "pdf") {
        const blob = data as Blob;
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        setSummaryData(data as ComplianceSummaryReport);
      }
    } catch {
      setError("Failed to generate compliance summary report.");
    } finally {
      setLoading(false);
    }
  };

  const loadSalePack = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBuildingSalePack();
      setSalePackData(data as SalePackData);
    } catch {
      setError("Failed to generate building sale pack.");
    } finally {
      setLoading(false);
    }
  };

  const loadAnnualReport = async (format: "json" | "pdf" = "json") => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAnnualDirectorReport(startDate, endDate, format);
      if (format === "pdf") {
        const blob = data as Blob;
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        setAnnualData(data as AnnualDirectorReportData);
      }
    } catch {
      setError("Failed to generate annual director report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance Reports"
        description="Generate compliance summaries, building sale packs, and annual director reports."
      />

      {error ? <ErrorAlert message={error} /> : null}
      {loading ? <Spinner /> : null}

      {/* Compliance Summary Report */}
      <ReportCard
        title="Compliance Summary"
        description="Current compliance posture grouped by category with status counts."
        onGenerate={() => loadSummary("json")}
        onPdf={() => loadSummary("pdf")}
        loading={loading}
      />
      {summaryData && (
        <div className="bg-white shadow rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Total" value={summaryData.summary.total} />
            <MiniStat
              label="Compliant"
              value={summaryData.summary.compliant}
              tone="green"
            />
            <MiniStat
              label="Due Soon"
              value={summaryData.summary.dueSoon}
              tone="amber"
            />
            <MiniStat
              label="Overdue"
              value={summaryData.summary.overdue}
              tone="red"
            />
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {summaryData.categories.length} categories,{" "}
            {summaryData.summary.total} items
          </div>
        </div>
      )}

      {/* Building Sale Pack */}
      <ReportCard
        title="Building Sale Pack"
        description="Key compliance documents for property sales — insurance, FRA, EICR, legionella, service contracts."
        onGenerate={loadSalePack}
        loading={loading}
      />
      {salePackData && (
        <div className="bg-white shadow rounded-lg p-4">
          <SalePackResult data={salePackData} />
        </div>
      )}

      {/* Annual Director Report */}
      <div className="bg-white shadow rounded-lg p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">
              Annual Director Report
            </h3>
            <p className="text-sm text-gray-500">
              Compliance performance, inspection breakdown, outstanding actions
              and major risks.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="form-label">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadAnnualReport("json")}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
            >
              Generate
            </button>
            <button
              onClick={() => loadAnnualReport("pdf")}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md disabled:opacity-50"
            >
              PDF
            </button>
          </div>
        </div>
      </div>
      {annualData && (
        <div className="bg-white shadow rounded-lg p-4">
          <AnnualReportResult data={annualData} />
        </div>
      )}
    </div>
  );
};

const ReportCard: React.FC<{
  title: string;
  description: string;
  onGenerate: () => void;
  onPdf?: () => void;
  loading: boolean;
}> = ({ title, description, onGenerate, onPdf, loading }) => (
  <div className="bg-white shadow rounded-lg p-4 flex items-center justify-between">
    <div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
    <div className="flex gap-2">
      <button
        onClick={onGenerate}
        disabled={loading}
        className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
      >
        Generate
      </button>
      {onPdf && (
        <button
          onClick={onPdf}
          disabled={loading}
          className="px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md disabled:opacity-50"
        >
          PDF
        </button>
      )}
    </div>
  </div>
);

const MiniStat: React.FC<{
  label: string;
  value: number;
  tone?: "green" | "amber" | "red";
}> = ({ label, value, tone }) => {
  const toneClass =
    tone === "green"
      ? "text-green-700"
      : tone === "amber"
      ? "text-amber-700"
      : tone === "red"
      ? "text-red-700"
      : "text-gray-900";
  return (
    <div className="text-center p-2 bg-gray-50 rounded">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
};

const SalePackResult: React.FC<{
  data: {
    entries: Array<{
      category: string;
      status: string;
      fileName?: string;
      downloadUrl?: string;
    }>;
  };
}> = ({ data }) => (
  <div className="space-y-2">
    <h4 className="text-sm font-medium text-gray-700">
      Building Sale Pack Contents
    </h4>
    <div className="divide-y divide-gray-100">
      {data.entries.map((entry, i) => (
        <div key={i} className="py-2 flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-900 capitalize">
              {entry.category.replace(/-/g, " ")}
            </span>
            {entry.fileName && (
              <span className="ml-2 text-xs text-gray-500">
                {entry.fileName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                entry.status === "Available"
                  ? "bg-green-100 text-green-800"
                  : entry.status === "Not Available"
                  ? "bg-gray-100 text-gray-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {entry.status}
            </span>
            {entry.downloadUrl && (
              <a
                href={entry.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-700 hover:text-primary-900 text-xs font-medium"
              >
                Download
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AnnualReportResult: React.FC<{ data: Record<string, unknown> }> = ({
  data,
}) => {
  const inspections = data.inspections as
    | { total: number; pass: number; fail: number; advisory: number }
    | undefined;
  const majorRisks = data.majorRisks as
    | Array<{ name: string; category: string; daysOverdue: number }>
    | undefined;

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-700">
        Annual Director Report
      </h4>
      {inspections && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Total Inspections" value={inspections.total} />
          <MiniStat label="Pass" value={inspections.pass} tone="green" />
          <MiniStat label="Fail" value={inspections.fail} tone="red" />
          <MiniStat
            label="Advisory"
            value={inspections.advisory}
            tone="amber"
          />
        </div>
      )}
      {majorRisks && majorRisks.length > 0 && (
        <div>
          <h5 className="text-xs font-medium text-red-700 uppercase tracking-wide mb-2">
            Major Risks
          </h5>
          <ul className="space-y-1">
            {majorRisks.map((r, i) => (
              <li key={i} className="text-sm text-gray-700">
                <span className="font-medium">{r.name}</span> ({r.category}) —{" "}
                {r.daysOverdue} days overdue
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ComplianceReports;
